<template>
	<card>
		<template slot="header">
			<h5 v-if="$slots.title || title" class="card-title">
				<slot name="title">
					{{title}}
				</slot>
			</h5>
		</template>
		<div>
			<div class="loading-spinner" v-if="isLoading">
				<vue-loaders-ball-spin-fade-loader color="white" scale="1.2"/>
			</div>
			<div class="ct-chart" v-else>
				<v-chart class="chart" :option="option"/>
				<div class="evaluation-text">
					<div v-if="isIncrease">
						<span class="increase-percent">▲ {{ percent }}%</span>
						<br/>
						<span class="explain-text">INCREASE IN COST</span>
					</div>
					<div v-else>
						<span class="decrease-percent">▼ {{ percent }}%</span>
						<br/>
						<span class="explain-text">DECREASE IN COST</span>
					</div>
				</div>
			</div>
		</div>
	</card>
</template>
<script>

import Card from "./Card.vue";
import { mapActions, mapGetters } from "vuex";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import {
	TitleComponent,
	TooltipComponent,
	LegendComponent,
	GridComponent
} from "echarts/components";
import VChart  from "vue-echarts";
import QuerySetting from '@/mixins/querySetting';
import OctoberData from '@/mixins/octoberData';

use([
	CanvasRenderer,
	BarChart,
	TitleComponent,
	TooltipComponent,
	LegendComponent,
	GridComponent
]);


export default {
	name: "stats-card",
	mixins: [QuerySetting,OctoberData],
	props: {
		title: {
			type: String,
			default: ""
		}
	},
	components: {
		Card,
		VChart
	},
	computed: {
		...mapGetters({
			periodTime: 'settime/getPeriodTime',
			currentDayPower			: 'influx/getCurrentDayPower',
			currentNightPower		: 'influx/getCurrentNightPower',
			prevDayPower				: 'influx/getPrevDayPower',
			prevNightPower			: 'influx/getPrevNightPower',
			predictDayPower			: 'influx/getPredictDayPower',
			predictNightPower		: 'influx/getPredictNightPower',
			user: 'auth/getUser',
			defaultSiteId: 'auth/getDefaultSiteId',
			defaultSiteName: 'auth/defaultSiteName'
		}),
	},
	data() {
		return {
			isLoading: true,
			currentCost: 0,
			pastCost: 0,
			predictCost: 0,
			isIncrease: false,
			tariff: {
				day: 0,
				night: 0,
				gen: 0
			},
			percent: 0,
			option: {
				tooltip: {
					trigger: 'axis',
					axisPointer: {
						type: 'shadow'
					},
					backgroundColor: "#484E69",
					borderColor: "#484E69",
					textStyle: {
						color: 'white'
					},
					formatter: '{b}, £{c}'
				},
				grid: {
					width: '36%',
					height: '76%',
					top: 30,
					containLabel: true
				},
				xAxis: [
					{
						type: 'category',
						data: [],
						axisLabel: {
							show: true,
							textStyle: {
								color: 'white',
								fontSize: '14px',
								fontWeight: 200,
								fontFamily: 'Nunito Sans Light'
							}
						},
						silent: true,
						axisLine: {
							show: false,
							symbol: 'none',
							lineStyle: {
								type: 'dashed'
							}
						},
						axisTick: {
							show: false
						}
					}
				],
				yAxis: [
					{
						type: 'value',
						axisLabel: {
							show: false
						},
						splitLine: {
							lineStyle: {
								color: 'rgba(255, 255, 255, 0.3)',
								type: 'dashed',
								width: 1
							}
						},
						splitNumber: 4,
					}
				],
				series: [
					{
						name: 'Direct',
						type: 'bar',
						barWidth: '80%',
						stack: 'cost',
						label: {
							color: 'white',
							fontSize: '15px',
							fontFamily: 'Nunito Sans Light',
							show: true,
							position: 'top',
							formatter: function(d) {
								return `£${d.value}` ;
							}
						},
						itemStyle: {
							opacity: 0.8
						},
						data: []
					},
					{
						name: 'Direct',
						type: 'bar',
						barWidth: '80%',
						stack: 'cost',
						label: {
							color: 'white',
							fontSize: '15px',
							fontFamily: 'Nunito Sans Light',
							show: true,
							position: 'top',
						},
						itemStyle: {
							opacity: 0.8
						},
						data: []
					}
				]
			}
		}
	},
	watch: {
		async periodTime(newValue) {
			await this.getTariff(this.user.siteId);
			this.processData(newValue);
		},
		async defaultSiteId(newValue) {
			await this.getTariff(this.user.siteId);
			this.processData(this.periodTime);
		}
	},
	async mounted() {
		await this.getTariff(this.user.siteId);
		if (this.user.rate == null) {
			this.$toasted.show("Please set rate first, you did not set rate.", {
				type: "error",
				duration: 2500,
			});
		}
		
		this.processData(this.periodTime);

		this.$nextTick(function () {
            window.setInterval(() => {
				this.processData(this.periodTime);
            },120000);
        });
	},
	methods: {
		...mapActions({
			getDisplayData: 'influx/getDisplayData',
			getRates: "admin/getRates",
		}),
		async getTariff(site) {
			const rates = await this.getRates(site);
			this.tariff = {
				day: rates[rates.length - 1].cost_1,
				night: rates[rates.length - 1].cost_2,
				gen: rates[rates.length - 1].cost_3
			}
		},
		async processData(value) {
			await this.getCostData(value);
			this.calcPercent();
			this.makeXAxisLabels(value);
		},
		async getCostData(value) {
			this.isLoading = true;
			await this.getDisplayData('overall');

			let dayTotal = 0.0;
			let nightTotal = 0.0;
			let currentDayCost = 0.0;
			let currentNightCost = 0.0;
			let prevDayCost = 0.0;
			let prevNightCost = 0.0;
			let predictDayCost = 0.0;
			let predictNightCost = 0.0;

			let _currentDayPower = this.currentDayPower;
			let _currentNightPower = this.currentNightPower;
			let _prevDayPower = this.prevDayPower;
			let _prevNightPower = this.prevNightPower;
			let _predictDayPower = this.predictDayPower;
			let _predictNightPower = this.predictNightPower;

			_currentDayPower.forEach(d => dayTotal += d[1]);
			currentDayCost = dayTotal * this.tariff.day;

			_currentNightPower.forEach(d => nightTotal += d[1]);
			currentNightCost = nightTotal * this.tariff.night;

			let currentCost = currentDayCost + currentNightCost;
			this.currentCost = Math.round((currentCost + Number.EPSILON) * 100) / 100 ;

			dayTotal = 0.0, nightTotal = 0.0;
			_prevDayPower.forEach(d => dayTotal += d[1]);
			prevDayCost = dayTotal * this.tariff.day;

			_prevNightPower.forEach(d => nightTotal += d[1]);
			prevNightCost = nightTotal * this.tariff.night;
			let pastCost = prevDayCost + prevNightCost;
			this.pastCost = Math.round((pastCost + Number.EPSILON) * 100) / 100;

			dayTotal = 0.0, nightTotal = 0.0;
			_predictDayPower.forEach(d => dayTotal += d[1]);
			predictDayCost = dayTotal * this.tariff.day;

			_predictNightPower.forEach(d => nightTotal += d[1]);
			predictNightCost = nightTotal * this.tariff.night;
			let predictCost = predictDayCost + predictNightCost;
			this.predictCost =  Math.round((predictCost + Number.EPSILON) * 100) / 100;
		},
		calcPercent() {
			this.option.series[0].data = [];
			this.option.series[1].data = [];
			this.option.series[0].data.push(
				{value: this.pastCost, itemStyle: {color: '#3D9293'}},
				{value: this.currentCost, itemStyle: {color: '#58E2C2'}}
			);
		 
			let total = (this.currentCost + this.predictCost).toFixed(2);
			if (this.predictCost > 0 ) {
				this.option.series[1].data.push(
					{},
					{value: this.predictCost, itemStyle: {
						color: 'transparent',
						borderColor: '#58E2C2',
						borderType: 'dashed'
					}}
				);
				this.option.series[1].label.formatter = `£${total}`;
			}

			if (parseFloat(this.pastCost ) > 0 && parseFloat(this.currentCost) > 0) {
				if (parseFloat(this.currentCost + this.predictCost ) > parseFloat(this.pastCost)) {
					this.isIncrease = true;
					this.percent = ((this.currentCost + this.predictCost - this.pastCost) / this.pastCost * 100).toFixed(2);
				} else {
					this.isIncrease = false;
					this.percent = ((this.pastCost  - this.currentCost -  this.predictCost) / this.pastCost * 100).toFixed(2);
				}
			} else if (parseFloat(this.pastCost) === 0 && parseFloat(this.currentCost) > 0){
				this.percent = 0;
				this.percent = this.currentCost;
			} else {
				this.percent = 0;
			}

			this.isLoading = false;
		},
		makeXAxisLabels(period) {
			let monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
			this.option.xAxis[0].data = [];
			let today = new Date();
			let month = today.getMonth();
			let current, past;

			switch (period) {
				case 'today':
					let date = today.getDate();
					current = `${monthNames[month]} ${date}`;

					today.setDate(date - 1);
					let lastDay = today.getDate();
					past = `${monthNames[month]} ${lastDay}`;
					break;
				case 'month':
					current = `${monthNames[month]}`;
					if (month - 1 < 0) {
						past = 'Dec'
					} else {
						past = `${monthNames[month-1]}`
					}
					break;
				case 'year':
					current = today.getFullYear();
					past = current - 1;
					break;
			}

			this.option.xAxis[0].data.push(past, current);
		}
	}
};
</script>
<style lang="scss" scoped>
.evaluation-text {
	position: absolute;
	top: 30%;
	right: 5%;
	text-align: center;
	.increase-percent {
		color: #E8506B;
		font-size: 28px;
	}

	.decrease-percent {
		color: #B4F9A1;
		font-size: 28px;
	}

	.explain-text {
		font-size: 13px;
		padding-left: 5px;
	}
}
</style>
